import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AuthShell } from '../../components/layout/AuthShell';
import { Eye, EyeOff, LogIn, Mail, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setEmailNotVerified(false);
    setResendSuccess(false);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      const statusCode = err.response?.status;

      if (statusCode === 403) {
        if (errorDetail.includes('E-Mail-Adresse')) {
          setEmailNotVerified(true);
          setError('Bitte bestätige zuerst deine E-Mail-Adresse.');
        } else {
          navigate('/registrierung-geprueft');
          return;
        }
      } else {
        setError(errorDetail || 'Anmeldung fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResendSuccess(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Senden des Codes');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <AuthShell
      testId="login-page"
      eyebrow="Kundenlogin"
      title={<>Willkommen <span className="serif-it text-purple">zurück.</span></>}
      intro="Melde dich in deinem Konto an"
      below={
        <Link to="/" className="text-ink/60 hover:text-ink" data-testid="login-back-home">
          ← Zurück zur Startseite
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="email" className="mb-2 block">E-Mail-Adresse</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre@email.de"
            required
            data-testid="login-email"
          />
        </div>

        <div>
          <Label htmlFor="password" className="mb-2 block">Passwort</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="pr-12"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 hover:text-ink"
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              data-testid="login-toggle-password"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <div className="mt-2 text-right">
            <Link to="/passwort-vergessen" className="text-purple font-medium hover:underline" data-testid="forgot-password-link">
              Passwort vergessen?
            </Link>
          </div>
        </div>

        {error && (
          <p className="text-red-700 font-medium" data-testid="login-error">{error}</p>
        )}

        {emailNotVerified && (
          <div className="bg-amber-50 border-2 border-amber-300 p-4" data-testid="login-resend-box">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-900 mb-3">
                  Prüfe deinen Posteingang oder fordere einen neuen Code an.
                </p>
                {resendSuccess ? (
                  <p className="text-green-700 font-medium" data-testid="login-resend-success">
                    ✓ Ein neuer Code wurde an {email} gesendet!
                  </p>
                ) : (
                  <Button
                    type="button"
                    variant="lilac"
                    size="sm"
                    onClick={handleResendCode}
                    disabled={resendLoading}
                    data-testid="login-resend-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
                    {resendLoading ? 'Wird gesendet...' : 'Code erneut senden'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <Button type="submit" disabled={loading} className="btn--block" data-testid="login-submit">
          {loading ? 'Anmelden...' : (<><LogIn className="w-5 h-5" />Anmelden</>)}
        </Button>
      </form>

      <p className="mt-6 text-center text-ink/80">
        Noch kein Konto?{' '}
        <Link to="/register" className="text-purple font-bold hover:underline" data-testid="login-register-link">
          Jetzt registrieren
        </Link>
      </p>
    </AuthShell>
  );
};

export default LoginPage;
